import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { SUPPORTED_LANGS, DEFAULT_LANG, isLang, RTL_LANGS, type Lang } from "@majourneys/shared";
import en from "./i18n/locales/en.json";
import frUrl from "./i18n/locales/fr.json?url";
import arUrl from "./i18n/locales/ar.json?url";
import esUrl from "./i18n/locales/es.json?url";
import itUrl from "./i18n/locales/it.json?url";
import deUrl from "./i18n/locales/de.json?url";
import nlUrl from "./i18n/locales/nl.json?url";

export const SUPPORTED_LOCALES = SUPPORTED_LANGS;
export type SupportedLocale = Lang;
export const RTL_LOCALES = RTL_LANGS;
export const DEFAULT_LOCALE: SupportedLocale = DEFAULT_LANG;

export function isValidLocale(lang: string): lang is SupportedLocale {
  return isLang(lang);
}

function getInitialLocale(): SupportedLocale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const firstSegment = window.location.pathname.split("/").filter(Boolean)[0];
  return firstSegment && isValidLocale(firstSegment) ? firstSegment : DEFAULT_LOCALE;
}

type LocaleBundle = Record<string, unknown>;
type FetchedLocale = Exclude<SupportedLocale, "en">;

/**
 * Hashed static-asset URL of each non-English locale file. The browser fetches
 * the page's locale from here before hydrating; `public/_headers` marks
 * /assets/* immutable so a returning visitor never downloads it twice.
 */
export const LOCALE_URLS: Readonly<Record<FetchedLocale, string>> = {
  fr: frUrl,
  ar: arUrl,
  es: esUrl,
  it: itUrl,
  de: deUrl,
  nl: nlUrl,
};

// ---------------------------------------------------------------------------
// Locale registry
//
// English is bundled; it is the fallback and the default language, so it is
// needed on every page. The other six are ~130 KB of JSON each. Shipping all
// seven inside the main script cost every visitor ~200 KB gzipped before the
// page could respond to a tap, so:
//   - the server registers all seven at startup (see src/server.ts),
//   - the test suite does the same (see src/test-setup.ts),
//   - the browser registers English here and fetches the rest with loadLocale.
// ---------------------------------------------------------------------------

const registry: Partial<Record<SupportedLocale, LocaleBundle>> = { en };

export function registerLocaleResources(
  bundles: Partial<Record<SupportedLocale, LocaleBundle>>,
): void {
  for (const [lang, bundle] of Object.entries(bundles)) {
    if (!bundle || !isValidLocale(lang)) continue;
    registry[lang] = bundle;
    i18n.addResourceBundle(lang, "translation", bundle, true, true);
  }
}

export function hasLocaleResources(lang: SupportedLocale): boolean {
  return registry[lang] !== undefined;
}

const inflight = new Map<SupportedLocale, Promise<void>>();

/**
 * Make `lang` translatable in the browser. Resolves immediately when the
 * strings are already here; otherwise fetches them once (concurrent callers
 * share the request). Never rejects: on failure the page keeps rendering in
 * English, the error is logged, and the next call retries.
 */
export function loadLocale(lang: SupportedLocale): Promise<void> {
  if (registry[lang]) return Promise.resolve();
  const pending = inflight.get(lang);
  if (pending) return pending;

  const url = LOCALE_URLS[lang as FetchedLocale];
  const request = fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json() as Promise<LocaleBundle>;
    })
    .then((bundle) => {
      registerLocaleResources({ [lang]: bundle });
    })
    .catch((err: unknown) => {
      console.error(`[i18n] could not load "${lang}" strings, staying on English`, err);
    })
    .finally(() => {
      inflight.delete(lang);
    });
  inflight.set(lang, request);
  return request;
}

/**
 * Load `lang`'s strings, then switch the browser singleton to it. Every
 * client-side language change goes through here (layout beforeLoad, header
 * switcher, mobile dropdown): switching first and loading later would render
 * English fallbacks under a non-English URL until the file arrived.
 */
export async function switchLanguage(lang: SupportedLocale): Promise<void> {
  await loadLocale(lang);
  if (i18n.language !== lang) await i18n.changeLanguage(lang);
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: getInitialLocale(),
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
  // Re-render translated components when a locale bundle lands after mount,
  // so a late-arriving file can never leave English on screen.
  react: { bindI18nStore: "added" },
});

// ---------------------------------------------------------------------------
// Per-locale instances (SSR safety)
//
// The default-export singleton above is mutated by `changeLanguage` — safe in
// the browser (one user, one language) but NOT in the Cloudflare Worker,
// where concurrent SSR requests share module state: a /de render could read
// Italian strings if an /it request switched the singleton mid-flight, and
// the edge cache would then freeze the wrong-language HTML publicly.
//
// On the server every request must resolve translations through a per-locale
// instance. Instances are created once per isolate per locale and never
// change language, so concurrent requests cannot interfere.
// ---------------------------------------------------------------------------

const localeInstances = new Map<SupportedLocale, typeof i18n>();

export function createLocaleInstance(lang: SupportedLocale): typeof i18n {
  const existing = localeInstances.get(lang);
  if (existing) return existing;
  const bundle = registry[lang];
  // Throw rather than fall back: an English render tagged as "fr" would sail
  // past the Worker's language check and be cached for every French visitor.
  if (!bundle) {
    throw new Error(
      `[i18n] no resources registered for "${lang}". The server entry must call registerLocaleResources(ALL_LOCALE_RESOURCES).`,
    );
  }
  const instance = i18n.createInstance();
  instance.use(initReactI18next);
  // initAsync:false = synchronous init; the instance is ready before the
  // first render that uses it (creation happens lazily inside a request).
  void instance.init({
    resources: {
      en: { translation: en },
      ...(lang === "en" ? {} : { [lang]: { translation: bundle } }),
    },
    lng: lang,
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    initAsync: false,
  });
  localeInstances.set(lang, instance);
  return instance;
}

export function getI18n(lang: SupportedLocale): typeof i18n {
  // Browser: keep the mutable singleton so client-side language switching
  // (locale-dropdown / locale-currency-switcher) keeps working unchanged.
  if (typeof window !== "undefined") return i18n;
  return createLocaleInstance(lang);
}

export default i18n;
